import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import MoreIcon from '../../../assets/icons/more-horizontal-icon.svg';
import CustomFieldBadge from '../../../components/customfield-badge/CustomFieldBadge';
import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';
import CustomFieldTextDetailed from '../../../components/customfield-text-detailed/CustomFieldTextDetailed';
import DailyAlertCard from '../../../components/daily-alerts-card/DailyAlertsCard';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import OfficeNotesCard from '../../../components/office-notes-card/OfficeNotesCard';
import PracticeStatusBadge from '../../../components/practice-status-badge/PracticeStatusBadge';
import { useAskPatBeta } from '../../../context';
import { useBroadSpecialtyOptions } from '../../../hooks/useBroadSpecialtyOptions';
import { useCustomFields } from '../../../hooks/useCustomFields';
import { usePermissions } from '../../../hooks/usePermissions';
import { getPracticeById } from '../../../services/practices/getPracticeById';
import { analytics } from '../../../utils/analytics';
import { postOfficeHoursList } from '../../../services/practices/postOfficeHoursList';
import { getPracticeClosures } from '../../../services/clousures/practiceClosureService';
import { PracticeClosure } from '../../../types/closures/types';

import AskPatChat from '../../../components/ask-pat/AskPatChat';
import { useAuth } from '../../../auth/AuthContext';
import { OfficeHour, Practice, Provider } from '../../../types';
import { CustomField } from '../../../types/custom-fields/types';
import { extractBroadSpecialtyOptions } from '../../../utils/broadSpecialtyOptions';
import {
  findDailyAlertField,
  findOfficeNotesField,
  getClientIdWithFallback,
  getCustomFieldValue,
  getLabImagingDisplay,
  getLabImagingValue,
} from '../../../utils/customFieldUtils';
import { isExternalPractice } from '../../../utils/externalEntity';
import { getFluShotsStatus } from '../../../utils/fluShotsUtils';
import { constructPracticeAddress } from '../../../utils/practiceUtils';
import ExternalAffordance from '../../../components/external-indicator/ExternalAffordance';
import PracticeClosuresCard from './PracticeClosuresCard';
import PracticeResourceSection from './PracticeResourceSection';
import PracticeStatusBox from './PracticeStatusBox';

// Shared in-flight dedup for practice detail closure fetches.
// This prevents duplicate requests from overlapping triggers (e.g. dev StrictMode remounts,
// and immediate clinicClosureUpdated events while the initial request is still pending).
const practiceDetailClosuresInFlight = new Map<string, Promise<PracticeClosure[]>>();

const fetchPracticeDetailClosures = (practiceId: string): Promise<PracticeClosure[]> => {
  const existing = practiceDetailClosuresInFlight.get(practiceId);
  if (existing) return existing;

  const request = getPracticeClosures({ practiceIds: [practiceId] }).finally(() => {
    const current = practiceDetailClosuresInFlight.get(practiceId);
    if (current === request) {
      practiceDetailClosuresInFlight.delete(practiceId);
    }
  });

  practiceDetailClosuresInFlight.set(practiceId, request);
  return request;
};

const PracticeDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [practice, setPractice] = useState<Practice | undefined>(
    undefined,
  );
  const [providers, setProviders] = useState<Provider[] | undefined>(
    undefined,
  );
  const [officeHours, setOfficeHours] = useState<OfficeHour[]>([]);
  const [officeHoursLoading, setOfficeHoursLoading] = useState(false);
  const [practiceClosures, setPracticeClosures] = useState<PracticeClosure[] | null>(null);
  const [closuresLoading, setClosuresLoading] = useState(false);
  const duplicateProviders = React.useMemo<Record<string, string>>(() => ({}), []);

  // const [officeHours, setOfficeHours] = useState<
  //   FormattedOfficeHour[] | []
  // >([]);
  const [error, setError] = useState<string | null>('');
  const [address, setAddress] = useState<string>('');
  const externalTablespace = (
    practice as
      | (Practice & { externalTablespace?: string | null })
      | undefined
  )?.externalTablespace;
  const isPracticeExternal = isExternalPractice(practice);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { accessToken, isAuthenticated } = useAuth();

  // Get user permissions
  const {
    canEditPractices,
    isAdmin,
    isReady: permissionsReady,
  } = usePermissions();

  // Check if Ask PAT beta feature is enabled
  const { isAskPatEnabled } = useAskPatBeta();

  // Fetch custom fields once for the entire practice detail page
  const clientId = getClientIdWithFallback();
  const {
    customFields: practiceCustomFields,
    loading: practiceCustomFieldsLoading,
    error: practiceCustomFieldsError,
    refresh: refreshPracticeCustomFields,
  } = useCustomFields(clientId, 'Practice');
  const { customFields: providerCustomFields } = useCustomFields(
    clientId,
    'ProviderData',
  );

  // Primary source: dedicated hook for specialty options
  const {
    options: broadSpecialtyOptions,
    loading: broadSpecialtyLoading,
    error: broadSpecialtyError,
    refresh: refreshBroadSpecialtyOptions,
  } = useBroadSpecialtyOptions({ entityType: 'Practice', clientId });

  // Track a single retry attempt for specialties to avoid loops
  const specialtyRetryRef = useRef<boolean>(false);
  const customFieldsRetryRef = useRef<boolean>(false);

  // If custom fields are missing, trigger a refresh (handles deep link scenarios)
  useEffect(() => {
    if (
      !practiceCustomFields &&
      !practiceCustomFieldsLoading &&
      !customFieldsRetryRef.current &&
      clientId
    ) {
      customFieldsRetryRef.current = true;
      refreshPracticeCustomFields?.();
    }
  }, [
    practiceCustomFields,
    practiceCustomFieldsLoading,
    refreshPracticeCustomFields,
    clientId,
  ]);

  // Normalize helper for arrays or map-shaped custom fields
  const normalizeCustomFieldsToArray = (
    fields:
      | CustomField[]
      | Record<string, CustomField>
      | Record<string, unknown>
      | undefined,
  ): CustomField[] | undefined => {
    if (!fields) return undefined;
    if (Array.isArray(fields)) return fields;
    return Object.values(fields) as CustomField[];
  };

  // Compute specialtyOptions with proper fallback chain
  const specialtyOptions = React.useMemo(() => {
    // 1) Prefer hook-provided broad options (most reliable)
    if (broadSpecialtyOptions && broadSpecialtyOptions.length > 0) {
      return broadSpecialtyOptions;
    }

    // 2) Fallback: extract from custom fields (hook, then camelCase payload, then snake_case)
    const effectiveFields =
      normalizeCustomFieldsToArray(practiceCustomFields) ||
      normalizeCustomFieldsToArray(
        practice?.customFields as unknown as Record<string, unknown>,
      ) ||
      normalizeCustomFieldsToArray(
        practice?.custom_fields as unknown as Record<string, unknown>,
      );

    if (effectiveFields) {
      const extracted = extractBroadSpecialtyOptions(effectiveFields);
      if (extracted.length > 0) return extracted;
    }

    // Return empty array if nothing available
    return [];
  }, [
    broadSpecialtyOptions,
    practiceCustomFields,
    practice?.customFields,
    practice?.custom_fields,
  ]);

  const mixedProviderAgeRanges = React.useMemo(() => {
    if (!providers || providers.length === 0) {
      return { hasMixed: false, values: [] as string[] };
    }
    const values = providers
      .map((provider) =>
        getCustomFieldValue(provider.customFields, 'Age'),
      )
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
    const uniqueValues = Array.from(new Set(values));
    return {
      hasMixed: uniqueValues.length > 1,
      values: uniqueValues,
    };
  }, [providers]);

  // Determine if custom fields are still initializing (handles deep links)
  const isCustomFieldsInitializing = React.useMemo(() => {
    // If we have an explicit error, we're not "initializing" anymore, we're in an error state
    if (practiceCustomFieldsError) return false;

    if (practiceCustomFieldsLoading) return true;
    if (!practiceCustomFields || practiceCustomFields.length === 0)
      return true;

    const fieldsArray = normalizeCustomFieldsToArray(
      practiceCustomFields,
    );
    const officeNotesField = findOfficeNotesField(fieldsArray);
    const dailyAlertField = findDailyAlertField(fieldsArray);

    // Only return true if we don't have the required fields yet
    return !officeNotesField || !dailyAlertField;
  }, [
    practiceCustomFields,
    practiceCustomFieldsLoading,
    practiceCustomFieldsError,
  ]);

  // Loading state: true when no options yet AND sources are still loading or errored
  const isSpecialtyLoading =
    specialtyOptions.length === 0 &&
    (broadSpecialtyLoading ||
      practiceCustomFieldsLoading ||
      !practice ||
      !!broadSpecialtyError ||
      !!practiceCustomFieldsError);

  // If the initial load fails, trigger one retry for specialty options
  useEffect(() => {
    if (
      specialtyOptions.length === 0 &&
      !broadSpecialtyLoading &&
      !practiceCustomFieldsLoading &&
      (broadSpecialtyError || practiceCustomFieldsError) &&
      !specialtyRetryRef.current
    ) {
      specialtyRetryRef.current = true;
      refreshBroadSpecialtyOptions?.();
    }
  }, [
    specialtyOptions.length,
    broadSpecialtyLoading,
    practiceCustomFieldsLoading,
    broadSpecialtyError,
    practiceCustomFieldsError,
    refreshBroadSpecialtyOptions,
  ]);
  // Fetch office hours with proper error handling and cleanup
  const fetchOfficeHours = useCallback(async () => {
    if (!id) return;

    if (!isAuthenticated || !accessToken) {
      return;
    }

    try {
      setOfficeHoursLoading(true);

      const data = await postOfficeHoursList([id]);

      const officeHours = data?.data.office_hours || [];
      setOfficeHours(officeHours);
    } catch (error) {
      console.error('Failed to fetch office hours:', error);
      setOfficeHours([]); // Clear office hours on error
    } finally {
      setOfficeHoursLoading(false);
    }
  }, [id, accessToken, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && accessToken) {
      fetchOfficeHours();
    }
  }, [fetchOfficeHours, accessToken, isAuthenticated]);

  // Listen for office hours update events to refresh immediately after save
  useEffect(() => {
    const handleOfficeHoursUpdate = (
      event: CustomEvent<{ practiceId: string }>,
    ) => {
      // Only refresh if the updated practice matches the current practice
      if (event.detail.practiceId === id) {
        fetchOfficeHours();
      }
    };

    window.addEventListener(
      'officeHoursUpdated',
      handleOfficeHoursUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        'officeHoursUpdated',
        handleOfficeHoursUpdate as EventListener,
      );
    };
  }, [id, fetchOfficeHours]);

  // Fetch closures once for this practice
  // null = "parent is fetching" sentinel; children skip their own fetch while null
  useEffect(() => {
    if (!id) return;
    setClosuresLoading(true);
    fetchPracticeDetailClosures(id)
      .then(setPracticeClosures)
      .catch(() => setPracticeClosures([])) // [] on error — not null (null = still loading)
      .finally(() => setClosuresLoading(false));
  }, [id]);

  // Keep closures fresh after CRUD operations
  useEffect(() => {
    if (!id) return;
    const handleUpdate = (e: Event) => {
      const detail = (e as CustomEvent<{ practiceId: string }>).detail;
      if (detail?.practiceId === id) {
        fetchPracticeDetailClosures(id)
          .then(setPracticeClosures)
          .catch(() => {});
      }
    };
    window.addEventListener('clinicClosureUpdated', handleUpdate);
    return () => window.removeEventListener('clinicClosureUpdated', handleUpdate);
  }, [id]);


  const fetchPractice = useCallback(async () => {
    if (!id) return;

    // Prevent duplicate calls within minimum interval
    const now = Date.now();
    if (
      hasFetchedRef.current &&
      now - lastFetchTimeRef.current < MIN_REFRESH_INTERVAL
    ) {
      return;
    }

    if (!isAuthenticated || !accessToken) {
      console.warn(
        '[PracticeDetail] No access token found, skipping fetchPractice',
      );
      return;
    }

    lastFetchTimeRef.current = now;
    hasFetchedRef.current = true;

    setIsLoading(true);
    setError(null); // Clear error before refetching
    try {
      const { data } = await getPracticeById(id);
      setPractice(data?.practice);
      if (data?.providers) {
        setProviders(data?.providers);
      }
      analytics.trackEvent('view', 'practice');
    } catch (error: unknown) {
      console.error('Failed to fetch practice:', error);
      setError(error as string);
    } finally {
      setIsLoading(false);
    }
  }, [id, accessToken, isAuthenticated]);

  useEffect(() => {
    if (!id) return;

    // Only fetch if we haven't fetched yet for this id
    if (hasFetchedRef.current) return;

    if (isAuthenticated && accessToken) {
      fetchPractice();
    }
  }, [id, fetchPractice, accessToken, isAuthenticated]);

  // Scroll to top when component mounts or route changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);

  // Listen for provider termination events to refresh practice data
  useEffect(() => {
    const handleProviderTerminated = (
      event: CustomEvent<{ providerId: string; practiceId: string }>,
    ) => {
      // Only refresh if the terminated provider was associated with this practice
      if (event.detail.practiceId === id) {
        // Small delay to ensure backend has processed the termination
        // Use ref to avoid dependency on fetchPractice
        setTimeout(() => {
          fetchPracticeRef.current();
        }, 500);
      }
    };

    window.addEventListener(
      'providerTerminated',
      handleProviderTerminated as EventListener,
    );

    return () => {
      window.removeEventListener(
        'providerTerminated',
        handleProviderTerminated as EventListener,
      );
    };
  }, [id]); // Only depend on id, use ref for fetchPractice

  // Track last fetch time to prevent duplicate calls
  const lastFetchTimeRef = React.useRef<number>(0);
  const hasFetchedRef = React.useRef<boolean>(false);
  const fetchPracticeRef = React.useRef(fetchPractice);
  const MIN_REFRESH_INTERVAL = 2000; // Minimum 2 seconds between refreshes

  // Keep ref updated with latest fetchPractice function
  useEffect(() => {
    fetchPracticeRef.current = fetchPractice;
  }, [fetchPractice]);

  // Reset fetch tracking when id changes
  useEffect(() => {
    hasFetchedRef.current = false;
    lastFetchTimeRef.current = 0;
  }, [id]);

  useEffect(() => {
    if (practice) {
      const completeAddress = constructPracticeAddress(practice);
      setAddress(completeAddress);
    }
  }, [practice]);

  if (error) {
    return (
      <NoItemsFound
        mainMessage="Failed to load practice"
        detailMessage={
          'An error occurred while fetching the practice data. Please try again.'
        }
        refetch={fetchPractice}
      />
    );
  }

  if (isLoading || !isAuthenticated || !permissionsReady) {
    return <PageLoader />;
  }


  return (
    <>
      <Helmet>
        <title>
          {practice?.name
            ? `${practice.name} | Practice | Resource Matrix — Denim Health`
            : 'Practice | Resource Matrix — Denim Health'}
        </title>
      </Helmet>

      <Flex
        justifyContent={'space-between'}
        mt="2rem"
        ml={{ base: '1rem', md: '2rem' }}
        mr={{ base: '1rem', md: '2rem' }}
        mb="1.62rem"
      >
        <Breadcrumb separator={<ChevronRightIcon color="gray.500" />}>
          <BreadcrumbItem
            onClick={() => {
              navigate('/');
            }}
          >
            <BreadcrumbLink>
              <Text
                fontSize="0.875rem"
                fontWeight="400"
                lineHeight="1.25rem"
                color="gray.700"
              >
                Home
              </Text>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem
            onClick={() => {
              navigate('/practices');
            }}
          >
            <BreadcrumbLink>
              <Text
                fontSize="0.875rem"
                fontWeight="400"
                lineHeight="1.25rem"
                color="gray.700"
              >
                Practices
              </Text>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbItem isCurrentPage>
            <Skeleton isLoaded={!isLoading}>
              <BreadcrumbLink href="#">
                <Text
                  fontSize="0.875rem"
                  fontWeight="400"
                  lineHeight="1.25rem"
                  color="gray.700"
                >
                  {practice?.name || ''}
                </Text>
              </BreadcrumbLink>
            </Skeleton>
          </BreadcrumbItem>
        </Breadcrumb>
        <HStack spacing={2}>
          {(canEditPractices() || isAdmin()) && (
            <Menu isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
              <MenuButton
                as={IconButton}
                icon={<img src={MoreIcon} />}
                size="sm"
                aria-label="Options"
                borderRadius="0.375rem"
                background={'background.secondary'}
                width="2.5rem"
                height="2.5rem"
                cursor={'pointer'}
                border={isOpen ? '2px solid border' : 'none'}
                borderColor={isOpen ? 'border.subtle' : 'none'}
                transition="all 0.2s ease-in-out"
              ></MenuButton>
              <MenuList>
                {(canEditPractices() || isAdmin()) && (
                  <MenuItem
                    isDisabled={!practice}
                    onClick={() =>
                      practice &&
                      navigate(`/practices/edit/${practice.id}`)
                    }
                  >
                    Edit Practice
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    onClose();
                  }}
                >
                  Close
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </HStack>
      </Flex>

      {isLoading ? (
        <PageLoader />
      ) : (
        <>
          <Flex
            gap="1rem"
            ml={{ base: '1rem', md: '2rem' }}
            mr={{ base: '1rem', md: '2rem' }}
            mb="3.63rem"
            direction={{ base: 'column', lg: 'row' }}
          >
            <Flex
              p="1.5rem"
              flexDirection={'column'}
              alignItems={'flex-start'}
              gap="1rem"
              flex={{ base: '1 1 100%', lg: '0 0 75%' }}
              background={'background.surfaceTeritiary'}
              width={'100%'}
              borderRadius="0.5rem"
            >
              <Flex
                justifyContent={'space-between'}
                alignItems={'start'}
              >
                <VStack align={'left'}>
                  <Tooltip label={'Market Name'}>
                    <Text
                      color={'gray.600'}
                      fontSize={'0.875rem'}
                      fontWeight={'700'}
                      lineHeight={'1.05rem'}
                      onClick={() =>
                        navigate(`/markets/${practice?.marketId}`)
                      }
                      cursor="pointer"
                    >
                      {practice?.market
                        ? `${practice.market} Links`
                        : 'Unknown'}
                    </Text>
                  </Tooltip>
                  <HStack
                    alignItems={'center'}
                    gap="0.75rem"
                    justifyContent={'flex-start'}
                  >
                    <Text
                      fontSize={'1.5rem'}
                      fontWeight={'700'}
                      lineHeight={'1.995rem'}
                      color={'text.primary'}
                      cursor="pointer"
                    >
                      {practice?.name ? practice?.name : 'N/A'}
                    </Text>
                    {isPracticeExternal && (
                      <ExternalAffordance entityType="practice" size="md" />
                    )}
                    {externalTablespace && (
                      <Text
                        fontSize="0.875rem"
                        fontWeight="600"
                        lineHeight="1.25rem"
                        color="text.secondary"
                      >
                        Tablespace: {externalTablespace}
                      </Text>
                    )}
                    {practice && !isPracticeExternal && (
                      <PracticeStatusBadge
                        active={practice.active ?? true}
                        practiceOnPac={practice.practiceOnPac}
                        practiceHospitalFollowupPac={practice.practiceHospitalFollowupPac}
                        practiceSickCrossCoveragePac={practice.practiceSickCrossCoveragePac}
                      />
                    )}
                  </HStack>
                </VStack>
              </Flex>
              <VStack align="left">
                <Text
                  fontSize="1.125rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="text.primary"
                  maxWidth="100%"
                  wordBreak="break-word"
                  whiteSpace="normal"
                >
                  {address}
                </Text>
                {getCustomFieldValue(
                  practice?.customFields,
                  'Directions',
                )?.toLowerCase() !== 'nan' &&
                  getCustomFieldValue(
                    practice?.customFields,
                    'Directions',
                  ) && (
                    <Text
                      fontSize="0.875rem"
                      fontStyle="italic"
                      fontWeight="400"
                      lineHeight="1.25rem"
                      color="text.secondary"
                      maxWidth="100%"
                      wordBreak="break-word"
                      whiteSpace="normal"
                      mb="0.75rem"
                    >
                      {getCustomFieldValue(
                        practice?.customFields,
                        'Directions',
                      )}
                    </Text>
                  )}
              </VStack>

              {mixedProviderAgeRanges.hasMixed && (
                <Alert
                  status="warning"
                  borderRadius="md"
                  width="100%"
                >
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="0.875rem">
                      Mixed provider age ranges
                    </AlertTitle>
                    <AlertDescription fontSize="0.875rem">
                      Providers at this practice have different age
                      ranges:{' '}
                      {mixedProviderAgeRanges.values.join(', ')}.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {/* Office Notes, Daily Alerts, and Clinic Closures - Moved to top for prominence */}
              {isSpecialtyLoading ? (
                <Flex
                  direction="column"
                  gap="0.5rem"
                  align="center"
                  justify="center"
                  width="100%"
                  pt="0.75rem"
                  mt="0.75rem"
                  borderTop="1px solid"
                  borderColor="border.subtle"
                  minH="12rem"
                >
                  <Spinner color="blue.500" size="md" />
                  <Text fontSize="0.95rem" color="gray.700">
                    Loading specialties…
                  </Text>
                </Flex>
              ) : (
                <Flex
                  direction="column"
                  gap="1rem"
                  width="100%"
                  pt="0.75rem"
                  mt="0.75rem"
                  borderTop="1px solid"
                  borderColor="border.subtle"
                >
                  <OfficeNotesCard
                    practice={practice}
                    isLoading={isLoading}
                    customFields={practiceCustomFields}
                    specialtyOptions={specialtyOptions}
                    isSpecialtyOptionsLoading={isSpecialtyLoading}
                    isCustomFieldsLoading={isCustomFieldsInitializing}
                    customFieldsError={practiceCustomFieldsError}
                  />
                  <DailyAlertCard
                    practice={practice}
                    isLoading={isLoading}
                    customFields={practiceCustomFields}
                    specialtyOptions={specialtyOptions}
                    isSpecialtyOptionsLoading={isSpecialtyLoading}
                    isCustomFieldsLoading={isCustomFieldsInitializing}
                    customFieldsError={practiceCustomFieldsError}
                  />
                  {practice?.id && (
                    <PracticeClosuresCard
                      practiceId={practice.id}
                      practiceTimezone={practice.timezone}
                      practiceState={practice.state}
                      closures={practiceClosures}
                    />
                  )}
                </Flex>
              )}

              <Flex maxWidth="100%" wrap="wrap" gap="1rem">
                {/* Left Section */}
                <VStack
                  gap="0.5rem"
                  align="start"
                  flex={1}
                  minWidth={0}
                >
                  <CustomFieldBadge
                    customField="Backline"
                    fieldValue={`${
                      getCustomFieldValue(
                        practice?.customFields,
                        'Backline',
                      ) || '-'
                    }`}
                    bgColor="#FED7D7"
                    titleColor="#9B2C2C"
                    valueColor="#9B2C2C"
                  />
                  <Flex
                    gap="0.62rem"
                    flexWrap="wrap"
                    alignItems="center"
                  >
                    <CustomFieldBadge
                      customField="Escalation"
                      fieldValue={
                        getCustomFieldValue(
                          practice?.customFields,
                          'Manager-Line',
                        ) || '-'
                      }
                      bgColor="background.orange200"
                    />
                    <Text
                      fontSize="0.75rem"
                      fontWeight="700"
                      lineHeight="1rem"
                      color="text.primary"
                    >
                      {getCustomFieldValue(
                        practice?.customFields,
                        'Manager-Name',
                      ) || '-'}
                    </Text>
                    <Text
                      fontSize="0.75rem"
                      fontWeight="400"
                      lineHeight="1rem"
                      color="text.orange600"
                      wordBreak="break-word"
                    >
                      {getCustomFieldValue(
                        practice?.customFields,
                        'Manager-Email',
                      ) || '-'}
                    </Text>
                  </Flex>
                  <CustomFieldBadge
                    customField="Answering Service"
                    fieldValue={`${
                      getCustomFieldValue(
                        practice?.customFields,
                        'After-Hours',
                      ) || '-'
                    }`}
                    bgColor="background.cyan100"
                  />
                  <CustomFieldBadge
                    customField="Phone"
                    fieldValue={`${
                      getCustomFieldValue(
                        practice?.customFields,
                        'Phone',
                      ) || '-'
                    }`}
                    bgColor="background.gray50"
                  />
                  <CustomFieldBadge
                    customField="Fax"
                    fieldValue={`${
                      getCustomFieldValue(
                        practice?.customFields,
                        'Fax',
                      ) || '-'
                    }`}
                    bgColor={'background.gray50'}
                  />
                  <CustomFieldBadge
                    customField="Medical Records Number"
                    fieldValue={`${
                      getCustomFieldValue(
                        practice?.customFields,
                        'Medical-Records-Number',
                      ) || '-'
                    }`}
                    bgColor={'background.gray50'}
                  />

                  <VStack
                    align={'flex-start'}
                    gap="0.75rem"
                    pt="0.75rem"
                    mt="0.75rem"
                    borderTop={'0.0625rem solid'}
                    borderColor={'border.subtle'}
                  >
                    <CustomFieldLabel
                      customField="Clinic NPI"
                      fieldValue={
                        getCustomFieldValue(
                          practice?.customFields,
                          'Clinic NPI',
                        ) || '-'
                      }
                      fieldValueBgColor="background.gray100"
                    />
                    <CustomFieldLabel
                      customField="Clinic Tax ID"
                      fieldValue={
                        getCustomFieldValue(
                          practice?.customFields,
                          'Group-TID',
                        ) || '-'
                      }
                      fieldValueBgColor="background.gray100"
                    />
                    {(() => {
                      const fluShotsValue = getCustomFieldValue(
                        practice?.customFields,
                        'Flu-Shots',
                      );
                      const { displayText, bgColor } =
                        getFluShotsStatus(fluShotsValue);

                      return (
                        <CustomFieldLabel
                          customField="Flu shots"
                          fieldValue={displayText}
                          fieldValueBgColor={bgColor}
                        />
                      );
                    })()}
                    <CustomFieldLabel
                      customField="EKG"
                      fieldValue={
                        getCustomFieldValue(
                          practice?.customFields,
                          'Ekg',
                        ) || '-'
                      }
                      fieldValueBgColor={(() => {
                        const val = getCustomFieldValue(
                          practice?.customFields,
                          'Ekg',
                        );
                        if (val?.toLowerCase() === 'yes') {
                          return 'background.teal100';
                        } else if (val?.toLowerCase() === 'no') {
                          return 'background.red100';
                        }
                        return 'background.gray100';
                      })()}
                    />
                    {(
                      [
                        ['Labs Hours', 'Labs-Drawn-Hours'],
                        ['Imaging Hours', 'Imaging-Available-Hours'],
                      ] as const
                    ).map(([label, fieldName]) => {
                      const { displayText, bgColor } =
                        getLabImagingDisplay(
                          getLabImagingValue(
                            practice?.customFields,
                            fieldName,
                          ),
                        );
                      return (
                        <CustomFieldLabel
                          key={fieldName}
                          customField={label}
                          fieldValue={displayText}
                          fieldValueBgColor={bgColor}
                        />
                      );
                    })}

                    <CustomFieldLabel
                      customField="Covid Screening While Scheduling"
                      fieldValue={
                        getCustomFieldValue(
                          practice?.customFields,
                          'Screen-For-Covid-When-Scheduling',
                        ) || '-'
                      }
                      fieldValueBgColor={(() => {
                        const fieldValue = getCustomFieldValue(
                          practice?.customFields,
                          'Screen-For-Covid-When-Scheduling',
                        );
                        if (!fieldValue) return 'background.gray100';

                        const normalized = fieldValue.toLowerCase();
                        if (
                          normalized === 'yes' ||
                          normalized === 'true'
                        )
                          return 'background.teal100';
                        if (
                          normalized === 'no' ||
                          normalized === 'false'
                        )
                          return 'background.red100';
                        return 'background.gray100';
                      })()}
                    />
                  </VStack>

                  {/* Cerner Fields */}
                  <VStack
                    align={'flex-start'}
                    gap="0.75rem"
                    pt="0.75rem"
                    mt="0.75rem"
                    borderTop={'0.0625rem solid'}
                    borderColor={'border.subtle'}
                  >
                    <Text
                      fontSize="0.875rem"
                      fontWeight="700"
                      lineHeight="1.25rem"
                      color="text.secondary"
                    >
                      Cerner Information
                    </Text>
                    <CustomFieldLabel
                      customField="Cerner Pool"
                      fieldValue={
                        getCustomFieldValue(
                          practice?.customFields,
                          'Cerner-pool',
                        ) || '-'
                      }
                      fieldValueBgColor="background.blue50"
                    />
                    <CustomFieldLabel
                      customField="Cerner Encounter Location"
                      fieldValue={
                        getCustomFieldValue(
                          practice?.customFields,
                          'Cerner-location',
                        ) || '-'
                      }
                      fieldValueBgColor="background.blue50"
                    />
                    <CustomFieldLabel
                      customField="Cerner Hub"
                      fieldValue={
                        getCustomFieldValue(
                          practice?.customFields,
                          'Cerner-Hub',
                        ) || '-'
                      }
                      fieldValueBgColor="background.blue50"
                    />
                  </VStack>
                </VStack>
                {practice?.timezone && (
                  <PracticeStatusBox
                    timezone={practice?.timezone || ''}
                    state={practice.state || ''}
                    unFormattedOfficeHours={officeHours}
                    isLoading={officeHoursLoading}
                    hideMoreDetails={false}
                    width={{ base: '100%', md: '30rem' }}
                    practiceId={practice?.id}
                    defaultOpen
                    clinicClosures={practiceClosures}
                    isClosuresLoading={closuresLoading}
                  />
                )}
                <Flex
                  justifyItems={'space-between'}
                  direction="row"
                  width={'100%'}
                  gap="1.5rem"
                  pt="0.75rem"
                  mt="0.75rem"
                  borderTop="1px solid"
                  borderColor={'border.subtle'}
                  mb="1rem"
                >
                  <CustomFieldTextDetailed
                    label="Clinical Staff"
                    value={`${
                      getCustomFieldValue(
                        practice?.customFields,
                        'Clinical-Staff',
                      ) || '-'
                    }`}
                  />
                  <CustomFieldTextDetailed
                    label="Front Staff"
                    value={`${
                      getCustomFieldValue(
                        practice?.customFields,
                        'Front-Staff',
                      ) || '-'
                    }`}
                  />
                  <CustomFieldTextDetailed
                    label="Director"
                    value={`${
                      getCustomFieldValue(
                        practice?.customFields,
                        'Director',
                      ) || '-'
                    }`}
                  />
                </Flex>
              </Flex>
            </Flex>
            <Flex
              flex="1"
              flexDirection="column"
              height={{ base: 'auto', lg: 'calc(100vh - 12rem)' }}
              gap="1rem"
            >
              {/* Ask PAT Chat Section - Takes remaining space */}
              {isAskPatEnabled && id && (
                <Box
                  flex="1"
                  minH="0"
                  width="100%"
                  maxWidth="100%"
                  overflow="hidden"
                  flexShrink={0}
                >
                  <AskPatChat
                    variant="embedded"
                    docType="practice"
                    docId={id}
                    docName={practice?.name}
                  />
                </Box>
              )}
            </Flex>
          </Flex>

          {practice?.name && id && (
            <PracticeResourceSection
              practiceId={id}
              name={practice.name}
              providers={providers}
              customFields={providerCustomFields}
              currentPractice={practice}
              isCustomFieldsLoading={isCustomFieldsInitializing}
              customFieldsError={practiceCustomFieldsError}
              specialtyOptions={specialtyOptions}
              isSpecialtyOptionsLoading={isSpecialtyLoading}
              duplicateProviders={duplicateProviders}
            />
          )}
        </>
      )}
    </>
  );
};

export { PracticeDetail };
